import { useAuth0 } from '@auth0/auth0-react'
import { useGetUserByEmail } from '@/operations/queries/getUserByEmail'
import { useCallback, useEffect, useMemo, useState, use } from 'react'
import { changeLanguage } from 'i18next'
import { LANGUAGE_STORAGE_KEY } from '@/lib/i18n'
import moment from 'moment'
import momentTZ from 'moment-timezone'
import 'moment/locale/de'
import 'moment/locale/da'
import 'moment/locale/fo'
import 'moment/locale/en-gb'
import { OrganisationList, useGetOrganisationList } from '@/operations/queries/getOrganisationList'
import { useGetUserPermissions } from '@/operations/queries/getUserPermissions'
import GlobalContext, { GlobalContextProps } from './GlobalContext'
import { exception } from '@/lib/applicationinsights'
import { getNavItems, RolesType } from '../navItems'
import { Ta_Permission_Type_Enum } from '@nemlia/hasura-gql/orgmgr/graphql'

// Define a type guard function to check if a value is of RolesType
const isRolesType = (value: unknown): value is RolesType => {
  return (
    typeof value === 'string' && ['developer', 'supplier', 'organisation_manager'].includes(value)
  )
}

export default function GlobalProvider({ children }) {
  const { user: authUser } = useAuth0()
  const authRole: RolesType | undefined = isRolesType(authUser['https://nemlia.com/roles'])
    ? authUser['https://nemlia.com/roles']
    : undefined

  const [pickedOrganisationId, setPickedOrganisationId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : localStorage.getItem('currentOrganisationId')
  )
  const [appliedLanguage, setAppliedLanguage] = useState<string | null>(null)

  const {
    data: uData,
    loading: uLoading,
    error: uError,
    refetch: uRefetch,
  } = useGetUserByEmail({ email: authUser.email })

  const {
    data: oData,
    loading: oLoading,
    error: oError,
    refetch: oRefetch,
  } = useGetOrganisationList()

  const user = uData?.ta_user[0]
  const userLanguage = user?.user_language || 'en'
  const languageLoading = Boolean(user) && appliedLanguage !== userLanguage
  const organisations = oData?.ta_organisation
  const currentOrganisation: OrganisationList[0] | undefined =
    !oLoading && !oError && organisations?.length
      ? organisations.find((o) => o.id === pickedOrganisationId) || organisations[0]
      : undefined

  const {
    data: pData,
    loading: pLoading,
    error: pError,
    refetch: pRefetch,
  } = useGetUserPermissions(user?.id)

  const permissions = useMemo(() => pData?.view_user_permissions ?? [], [pData])

  const tz = currentOrganisation?.tz || user?.user_timezone || momentTZ.tz.guess()

  const localeCollators = useMemo(() => {
    const locale = (user?.user_language === 'dk' ? 'da' : user?.user_language) || 'en'
    return {
      localeCompare: new Intl.Collator(locale, { numeric: true }).compare,
      localeCompareInsensitive: new Intl.Collator(locale, {
        numeric: true,
        sensitivity: 'base',
      }).compare,
    }
  }, [user?.user_language])

  // Set default timezone
  momentTZ.tz.setDefault(tz)

  // Change global moment lng when user lng is updated
  useEffect(() => {
    if (!user) {
      return
    }

    const momentLocaleConfig = {
      week: {
        dow: 0, // Sunday is the first day of the week for representation
      },
    }

    const momentLng = (user.user_language === 'dk' ? 'da' : user.user_language) ?? 'en'
    moment.updateLocale(momentLng, momentLocaleConfig)
    momentTZ.updateLocale(momentLng, moment.localeData()['_config']) // copy locale to moment-timezone

    localStorage.setItem(LANGUAGE_STORAGE_KEY, userLanguage)
    changeLanguage(userLanguage).then(() => setAppliedLanguage(userLanguage))
  }, [user, userLanguage])

  useEffect(() => {
    if (oLoading) {
      return
    } else if (currentOrganisation) {
      localStorage.setItem('currentOrganisationId', currentOrganisation.id)
    } else {
      localStorage.removeItem('currentOrganisationId')
    }
  }, [oLoading, currentOrganisation])

  const hasFeature = useCallback(
    (feature: Ta_Permission_Type_Enum, organisationId = currentOrganisation?.id) => {
      if (authRole === 'developer' || authRole === 'supplier') {
        return true
      }

      if (!organisationId) {
        return false
      }

      return permissions.some((p) => p.organisation_id === organisationId && p.type === feature)
    },
    [authRole, currentOrganisation?.id, permissions]
  )

  // Error tracking
  if (uError) {
    exception('Styra initial page load: Error getting user', {
      msg: uError.message,
    })
  }
  if (oError) {
    exception('Styra initial page load: Error getting organisationList', {
      msg: oError.message,
    })
  }
  if (pError) {
    exception('Styra initial page load: Error getting user permissions', {
      msg: pError.message,
    })
  }

  // Actions
  const onOrganisationChange = (id: string) => {
    setPickedOrganisationId(id)
  }

  const permissionsLoading = Boolean(pLoading || (user?.id && !pData && !pError))

  const contextProps: GlobalContextProps = {
    user: user
      ? {
          ...user,
          user_language: user?.user_language || 'en',
        }
      : undefined,
    organisations: organisations || [],
    currentOrganisation: currentOrganisation,
    setCurrentOrganisation: onOrganisationChange,
    permissions,
    hasFeature,
    refetch: {
      user: uRefetch,
      organisations: oRefetch,
      permissions: pRefetch,
    },
    language: appliedLanguage,
    pageLoading: Boolean(uLoading || oLoading || permissionsLoading || languageLoading),
    pageError: Boolean(uError || oError || !user),
    role: authRole,
    isManager: authRole === 'organisation_manager',
    navItems: getNavItems(authRole),
    ...localeCollators,
  }

  return <GlobalContext value={contextProps}>{children}</GlobalContext>
}

export function useGlobal() {
  const context = use(GlobalContext)
  if (context === undefined) {
    throw new Error('useItems must be used within a ItemsProvider')
  }
  return context
}
